import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LoginProviderPopup } from "../../common/components";
import { routes } from "../../constants/routes";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertComponent } from "../../common/components";
import { useSetLoading } from "../../common/hooks/appearance";
import { handleProviderLogin } from "../../common/routines/user-operations";
import { providerLogin } from "../../api/auth";

//  TODO: replace with your provider dashboard component
import ProviderDashboard from "./provider-dashboard/dashboard";

//  TODO: replace with provider login bg (or reuse admin bg)
import loginBackground from "../../assets/media/admin-login-bg-image.jpeg";

const ProviderSection: React.FC = () => {
  const providerDetails = useSelector(
    (state: any) => state.session.providerDetails
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);

  const checkIsProviderLogin = () => {
    const raw = localStorage.getItem("providerDetails");
    if (!raw) return false;

    const stored = JSON.parse(raw);

    // must have token
    if (!stored?.accessToken) return false;

    // if expiryTime exists, enforce it
    if (stored?.expiryTime && stored.expiryTime <= Date.now()) return false;

    return true;
  };


  const mapErrorMessage = (err: any): string => {
    const message = err?.response?.data?.message;
    const errors = err?.response?.data?.errors;

    if (message === "Input validation failed" && Array.isArray(errors)) {
      const messages: string[] = [];
      errors.forEach((errObj: any) => {
        if (errObj.email) messages.push("Invalid Email Address");
        if (errObj.password) messages.push("Invalid Password");
      });
      return messages.join(" and ");
    }

    //  Adjust backend message keys as per provider auth API
    switch (message) {
      case "PROVIDER_USER_NOT_FOUND":
        return "Provider user not found";
      case "PROVIDER_PASSWORD_NOT_SET":
        return "Password is not set for this provider user";
      case "INVALID_PROVIDER_CREDENTIALS":
        return "Invalid Password";
      case "Too many requests. Please try again later.":
        return "Too many requests. Please try again later.";
      default:
        return "Login failed. Please try again.";
    }
  };

  const handleLoginAPI = async (email: string, password: string) => {
    setLoading(true);
    setError("");

    try {
      const response = await providerLogin({ email, password });

      if (response?.data?.success === true && response?.data) {
        const userData = response?.data?.data;

        handleProviderLogin(userData);

        setTimeout(() => {
          navigate(routes.PROVIDER_DASHBOARD, { replace: true });
        }, 100);
      }
    } catch (err: any) {
      /**
       *  Backend-controlled error handling
       */
      const apiError = err?.response?.data;

      if (apiError?.errorCode === "ACCOUNT_LOCKED") {
        // 🔒 Account locked case
        setError(apiError.message);
        setAlertMessage(apiError.message);
        setAlertType("error");
        setAlertOpen(true);
        return;
      }

      /**
       * ❌ Fallback for other errors
       */
      const friendlyMessage =
        apiError?.message || mapErrorMessage(err) || "Login failed. Please try again.";

      setError(friendlyMessage);
      setAlertMessage(friendlyMessage);
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const reason = localStorage.getItem("providerLogoutReason");
    if (reason) {
      if (reason === "provider:INACTIVITY" || reason === "provider:SESSION_EXPIRED") {
        setAlertMessage("Your session has expired. Please log in again.");
        setAlertType("info");
      } else if (reason === "provider:ACCOUNT_INACTIVE") {
        setAlertMessage("Your account has been deactivated. Please contact support.");
        setAlertType("error");
      }

      if (
        reason === "provider:INACTIVITY" ||
        reason === "provider:SESSION_EXPIRED" ||
        reason === "provider:ACCOUNT_INACTIVE"
      ) {
        localStorage.removeItem("providerLogoutReason");

        dispatch({
          type: "SESSION:SET_AUTO_LOGOUT",
          payload: { userType: "provider", autoLogoutOfProvider: false },
        });

        setAlertOpen(true);

        setTimeout(() => {
          setAlertOpen(false);
        }, 3000);
      }
    }
  }, [providerDetails, dispatch]);

  useEffect(() => {
    // If already logged in and hit provider login page, redirect to provider dashboard
    if (
      checkIsProviderLogin() &&
      location.pathname !== routes.PROVIDER_DASHBOARD
    ) {
      navigate(routes.PROVIDER_DASHBOARD, { replace: true, state: {} });
    }

    // show logout success message after redirecting to login page
    if (location.state && (location.state as any)?.fromLogout) {
      setAlertMessage((location.state as any)?.alertMessage);
      setAlertType((location.state as any)?.alertType);
      setAlertOpen(true);

      setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} });
      }, 1000);
    }
  }, [location.pathname, location.state, navigate]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  // If not logged in, show login popup
  if (!checkIsProviderLogin()) {
    return (
      <>
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundImage: `url(${loginBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className="provider-login-wrapper"
        >
          <LoginProviderPopup
            open={!checkIsProviderLogin()}
            onClose={() => setError("")}
            onLogin={handleLoginAPI}
            loading={loading}
            error={error}
            headerText="MemorialGo Provider Portal"
            bodyText="Provider Login"
            bodyContent="Enter your credentials to continue"
            onForgotPassword={() => {
              setError(""); // optional - clears old login error
              navigate(routes.PROVIDER_FORGOT_PWD);
            }}
          />
        </div>

        <AlertComponent
          message={alertMessage}
          type={alertType ?? "info"}
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
        />
      </>
    );
  }

  // If logged in, show provider dashboard
  return (
    <div className="provider-dashboard">
      <ProviderDashboard />

      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default ProviderSection;
